import { useState } from "react";
import { t } from "@/i18n-js/instance";
import type { IconType } from "@/react/components/shared/Icon";
import { Icon } from "@/react/components/shared/Icon";
import {
  SPACE_DATE_RANGE,
  type SpaceDateRange,
} from "@/react/types/AiSummary/SpaceSummary";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { SummarizeButton } from "../SummarizeButton";

interface DropdownOption {
  disabled?: boolean;
  icon: IconType;
  label: string;
  value: SpaceDateRange;
}

export interface SummarizeDropdownProps {
  onClick: (range: SpaceDateRange) => void;
  options?: {
    firstUnreadMessageId?: string;
    isChatSpace?: boolean;
  };
}

export const SummarizeDropdown = ({
  onClick,
  options,
}: SummarizeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (value: SpaceDateRange, disabled?: boolean) => {
    if (!disabled) onClick(value);
  };

  const baseOptions: DropdownOption[] = [
    {
      label: t("ai_spaces_summaries.last_7_days"),
      value: SPACE_DATE_RANGE.LAST_7_DAYS,
      icon: "20-ai-summary-clock" as IconType,
    },
    {
      label: t("ai_spaces_summaries.last_month"),
      value: SPACE_DATE_RANGE.LAST_MONTH,
      icon: "20-ai-summary-clock" as IconType,
    },
  ];

  const unreadOption: DropdownOption[] = options?.isChatSpace
    ? [
        {
          label: t("ai_spaces_summaries.from_unread"),
          value: SPACE_DATE_RANGE.FROM_UNREAD,
          icon: "20-inbox-unread" as IconType,
          disabled: !options?.firstUnreadMessageId,
        },
      ]
    : [];

  const dropdownOptions = [...unreadOption, ...baseOptions];

  return (
    <Dropdown
      direction="bottom-end"
      menuItemsClassName="!w-40 flex flex-col [&>button]:!p-3"
      menuWrapperClassName="[&>div]:!p-2"
      button={
        <Dropdown.MenuButton>
          <SummarizeButton isOpen={isOpen} />
        </Dropdown.MenuButton>
      }
      onChange={value => onClick(value as SpaceDateRange)}
      onToggle={setIsOpen}
    >
      {dropdownOptions.map(({ label, value, icon, disabled }) => (
        <Dropdown.Item key={value} onClick={() => handleClick(value, disabled)}>
          <Dropdown.ItemLink type="button" disabled={disabled}>
            <div
              className={classNames(
                "text-default dark:text-dark flex items-center gap-x-2",
                {
                  "opacity-50": disabled,
                },
              )}
            >
              <Icon type={icon} size={20} useWithStrokeCurrentColor />
              <Typography.LabelSm>{label}</Typography.LabelSm>
            </div>
          </Dropdown.ItemLink>
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};
